import { Dropdown } from '@honeycomb-npm/honeycomb-react';
import { useRef } from 'react';

import { useAppSelector } from '../../../app/hooks';
import { selectTimeDurationMonths, selectTimeDurationYears } from '../genericBladeSelectors';

import {
    GenericBladeDynamicContentType,
    GenericBladeParsedDataType,
    GenericFormAEM,
    NodePayload,
} from '../genericBladeTypes';

type Props = {
    id: string;
    genericFormAEM: Record<string, GenericBladeDynamicContentType>;
    formValues: GenericBladeParsedDataType,
    handleChange: (node: string, value?: NodePayload) => void;
    errors: Record<string, string>;
}

const TimeDuration = ({
    id,
    genericFormAEM,
    formValues,
    handleChange,
    errors,
}: Props) => {

    const years = useAppSelector((state) => selectTimeDurationYears(state, id));
    const months = useAppSelector((state) => selectTimeDurationMonths(state, id));
    
    const dropdownRef = useRef<Record<string, HTMLSelectElement | null>>({
        years: null, months: null,
    });

    if (errors?.years && !formValues?.years) {
        dropdownRef.current.years?.focus();
    } else if (errors?.months && !formValues?.months) {
        dropdownRef.current.months?.focus();
    }

    const onYearSelectionchange = (name: string, value: NodePayload) => {
        handleChange(name, value);
        if (value === (years.length - 1).toString()) {
            handleChange('months', '0');
        }
    };

    const getMonths = () => formValues?.years?.value?.toString() === '0' ? months.filter(month => parseInt(month.value) > 0) : months;

    return (
        <div className="row m-0 p-0">
            <div className="col-12 col-md-6 pr-md-2 p-0 mb-1">
                <Dropdown
                    id={`${id}-time-duration-years-dropdown`}
                    name="years"
                    defaultOption={(genericFormAEM.years as GenericFormAEM)?.defaultValue ?? 'Select'}
                    options={years}
                    onChange={(name: string, value: string) =>
                        onYearSelectionchange(name, value)
                    }
                    value={formValues?.years?.value?.toString() ?? ''}
                    label={(genericFormAEM.years as GenericFormAEM)?.label ?? ''}
                    errormessage={errors?.years}
                    isValid={!errors?.years || !!formValues?.years?.value}
                    hideDefaultOption={!!formValues?.years?.value}
                    required={true}
                    ref={(el: HTMLSelectElement) =>
                        (dropdownRef.current.years = el)
                    }
                />
            </div>

            {(!formValues?.years?.value || Number(formValues?.years?.value) < years.length - 1) && (
                <div className="col-12 col-md-6 pr-md-2 p-0 mb-1">
                    {!(genericFormAEM.months as GenericFormAEM)?.label && <br className="d-none d-md-block" />}
                    <Dropdown
                        id={`${id}-time-duration-months-dropdown`}
                        name="months"
                        defaultOption={(genericFormAEM.months as GenericFormAEM)?.defaultValue ?? 'Select'}
                        options={getMonths()}
                        onChange={(name: string, value: string) =>
                            handleChange(name, value)
                        }
                        value={formValues?.months?.value?.toString() ?? ''}
                        label={(genericFormAEM.months as GenericFormAEM)?.label ?? ''}
                        errormessage={errors?.months}
                        isValid={!errors?.months || !!formValues?.months?.value}
                        hideDefaultOption={!!formValues?.months?.value}
                        required={true}
                        ref={(el: HTMLSelectElement) =>
                            (dropdownRef.current.months = el)
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default TimeDuration;
